// frontend/src/components/jobPlacement/PlacementFollowupForm.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import SignatureField from "../SignatureField";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "../../assets/css/signature.css";

const today = new Date().toISOString().split("T")[0];

const initialFormState = {
  placement_specialist: "",
  referring_counselor: "",
  date_placed: "",
  start_date: "",
  completion_date: "",
  closure_plan: "",
  employer_id: "",
  employer_name: "",
  employer_address: "",
  employer_phone: "",
  contact_person: "",
  immediate_supervisor: "",
  job_title: "",
  job_duties: "",
  hourly_wage: "",
  hours_worked_weekly: "",
  benefits_received: "",
  week1_date: "",
  week1_comments: "",
  week1_consumer_date: "",
  week1_specialist_date: "",
  days30_date: "",
  days30_comments: "",
  days30_consumer_date: "",
  days30_specialist_date: "",
  days60_date: "",
  days60_comments: "",
  days60_consumer_date: "",
  days60_specialist_date: "",
  days90_date: "",
  days90_comments: "",
  days90_consumer_date: "",
  days90_specialist_date: "",
};

// Helper function to format a date string for display (avoids timezone issues)
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString();
};

// Helper function to calculate follow-up dates based on start date
const calculateFollowupDates = (startDate) => {
  if (!startDate) return {};
  
  // Parse the date string directly to avoid timezone issues
  const [year, month, day] = startDate.split('-').map(Number);
  const start = new Date(year, month - 1, day); // month is 0-indexed
  
  // Week 1: 7 days after start
  const week1 = new Date(start);
  week1.setDate(week1.getDate() + 7);
  
  // 30 days after start
  const days30 = new Date(start);
  days30.setDate(days30.getDate() + 30);
  
  // 60 days after start
  const days60 = new Date(start);
  days60.setDate(days60.getDate() + 60);
  
  // 90 days after start
  const days90 = new Date(start);
  days90.setDate(days90.getDate() + 90);
  
  // Format date without timezone conversion
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  // Only return target dates, NOT signature dates
  return {
    week1_date: formatDate(week1),
    days30_date: formatDate(days30),
    days60_date: formatDate(days60),
    days90_date: formatDate(days90),
  };
};

const PlacementFollowupForm = ({
  followup,
  jobPlacement,
  consumer,
  onCancel,
  onSave,
}) => {
  const { user, tenant_id: tenantId } = useContext(AuthContext);
  const apiBase = getApiBaseURL();
  const userId = user?.user_id;

  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [showSignatures, setShowSignatures] = useState(false);
  const [employers, setEmployers] = useState([]);
  const [jobCoaches, setJobCoaches] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [entryMode, setEntryMode] = useState("existing"); // "existing" or "manual"
  const [jobDetailsCollapsed, setJobDetailsCollapsed] = useState(false);
  const [employerCollapsed, setEmployerCollapsed] = useState(false);

  // Signature states
  const [week1ConsumerSig, setWeek1ConsumerSig] = useState("");
  const [week1SpecialistSig, setWeek1SpecialistSig] = useState("");
  const [days30ConsumerSig, setDays30ConsumerSig] = useState("");
  const [days30SpecialistSig, setDays30SpecialistSig] = useState("");
  const [days60ConsumerSig, setDays60ConsumerSig] = useState("");
  const [days60SpecialistSig, setDays60SpecialistSig] = useState("");
  const [closureConsumerSig, setClosureConsumerSig] = useState("");
  const [closureSpecialistSig, setClosureSpecialistSig] = useState("");

  // Signature refs
  const week1ConsumerRef = useRef(null);
  const week1SpecialistRef = useRef(null);
  const days30ConsumerRef = useRef(null);
  const days30SpecialistRef = useRef(null);
  const days60ConsumerRef = useRef(null);
  const days60SpecialistRef = useRef(null);
  const closureConsumerRef = useRef(null);
  const closureSpecialistRef = useRef(null);

  // Load employers and job coaches
  useEffect(() => {
    if (!tenantId) return;

    // Load employers
    secureAxios
      .get(`${apiBase}/api/employers/dropdown`, {
        params: { tenant_id: tenantId },
      })
      .then((res) => setEmployers(res.data))
      .catch((err) => console.error("Error loading employers:", err));

    // Load job coaches
    secureAxios
      .get(`${apiBase}/api/users`, {
        params: { tenant_id: tenantId },
      })
      .then((res) => {
        const coaches = res.data.filter(
          (u) =>
            u.role?.toLowerCase() === "job coach" ||
            u.role?.toLowerCase() === "admin"
        );
        setJobCoaches(coaches);
      })
      .catch((err) => console.error("Error loading job coaches:", err));

    // Load all placements for tenant (filter by consumer if available, but also include unassigned)
    secureAxios
      .get(`${apiBase}/api/placements`, {
        params: {
          tenant_id: tenantId,
        },
      })
      .then((res) => {
        // Filter to show: placements for this consumer OR placements without a consumer assigned
        const relevantPlacements = res.data.filter(
          (p) => !p.consumer_id || p.consumer_id === consumer?.consumer_id
        );
        setPlacements(relevantPlacements);
      })
      .catch((err) => console.error("Error loading placements:", err));

    // For new followups, fetch prefill data to get referring counselor name
    if (!followup && jobPlacement?.job_placement_id) {
      secureAxios
        .get(`${apiBase}/api/placement-followups/prefill/${jobPlacement.job_placement_id}`, {
          params: { tenant_id: tenantId },
        })
        .then((res) => {
          const prefillData = res.data;
          // Pre-fill referring counselor and placement specialist from prefill data
          setFormData((prev) => ({
            ...prev,
            referring_counselor: prefillData.referring_counselor || prev.referring_counselor,
            placement_specialist: prefillData.placement_specialist || user?.name || prev.placement_specialist,
          }));
        })
        .catch((err) => console.error("Error loading prefill data:", err));
    }
  }, [tenantId, apiBase, consumer?.consumer_id, followup, jobPlacement?.job_placement_id, user?.name]);

  // Populate form when editing
  useEffect(() => {
    if (followup) {
      setFormData({
        placement_specialist: followup.placement_specialist || "",
        referring_counselor: followup.referring_counselor || "",
        date_placed: followup.date_placed?.split("T")[0] || "",
        start_date: followup.start_date?.split("T")[0] || "",
        completion_date: followup.completion_date?.split("T")[0] || "",
        closure_plan: followup.closure_plan || "",
        employer_id: followup.employer_id || "",
        employer_name: followup.employer_name || "",
        employer_address: followup.employer_address || "",
        employer_phone: followup.employer_phone || "",
        contact_person: followup.contact_person || "",
        immediate_supervisor: followup.immediate_supervisor || "",
        job_title: followup.job_title || "",
        job_duties: followup.job_duties || "",
        hourly_wage: followup.hourly_wage || "",
        hours_worked_weekly: followup.hours_worked_weekly || "",
        benefits_received: followup.benefits_received || "",
        week1_date: followup.week1_date?.split("T")[0] || "",
        week1_comments: followup.week1_comments || "",
        week1_consumer_date: followup.review_week1_consumer_date?.split("T")[0] || "",
        week1_specialist_date: followup.review_week1_specialist_date?.split("T")[0] || "",
        days30_date: followup.days30_date?.split("T")[0] || "",
        days30_comments: followup.days30_comments || "",
        days30_consumer_date: followup.review_30days_consumer_date?.split("T")[0] || "",
        days30_specialist_date: followup.review_30days_specialist_date?.split("T")[0] || "",
        days60_date: followup.days60_date?.split("T")[0] || "",
        days60_comments: followup.days60_comments || "",
        days60_consumer_date: followup.review_60days_consumer_date?.split("T")[0] || "",
        days60_specialist_date: followup.review_60days_specialist_date?.split("T")[0] || "",
        days90_date: followup.days90_date?.split("T")[0] || "",
        days90_comments: followup.days90_comments || "",
        days90_consumer_date: followup.review_closure_consumer_date?.split("T")[0] || "",
        days90_specialist_date: followup.review_closure_specialist_date?.split("T")[0] || "",
      });

      // Load signatures
      setWeek1ConsumerSig(followup.review_week1_consumer_signature || "");
      setWeek1SpecialistSig(followup.review_week1_specialist_signature || "");
      setDays30ConsumerSig(followup.review_30days_consumer_signature || "");
      setDays30SpecialistSig(followup.review_30days_specialist_signature || "");
      setDays60ConsumerSig(followup.review_60days_consumer_signature || "");
      setDays60SpecialistSig(followup.review_60days_specialist_signature || "");
      setClosureConsumerSig(followup.review_closure_consumer_signature || "");
      setClosureSpecialistSig(
        followup.review_closure_specialist_signature || ""
      );

      // Auto-show signatures if any exist
      if (
        followup.review_week1_consumer_signature ||
        followup.review_week1_specialist_signature ||
        followup.review_30days_consumer_signature ||
        followup.review_30days_specialist_signature ||
        followup.review_60days_consumer_signature ||
        followup.review_60days_specialist_signature ||
        followup.review_closure_consumer_signature ||
        followup.review_closure_specialist_signature
      ) {
        setShowSignatures(true);
      }
    } else {
      // For new forms, set initial defaults
      // The prefill API call in the other useEffect will provide the accurate values
      setFormData((prev) => ({
        ...prev,
        // Default placement specialist to current user (may be overwritten by prefill)
        placement_specialist: user?.name || "",
      }));
    }
  }, [followup, user]);

  // Calculate follow-up dates when start_date changes (only for new forms or when dates are empty)
  useEffect(() => {
    if (formData.start_date && !followup) {
      const calculatedDates = calculateFollowupDates(formData.start_date);
      setFormData((prev) => ({
        ...prev,
        // Only set target dates if they're currently empty (NOT signature dates)
        week1_date: prev.week1_date || calculatedDates.week1_date,
        days30_date: prev.days30_date || calculatedDates.days30_date,
        days60_date: prev.days60_date || calculatedDates.days60_date,
        days90_date: prev.days90_date || calculatedDates.days90_date,
      }));
    }
  }, [formData.start_date, followup]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle start_date change - recalculate follow-up dates
  const handleStartDateChange = (e) => {
    const { value } = e.target;
    const calculatedDates = calculateFollowupDates(value);
    setFormData((prev) => ({
      ...prev,
      start_date: value,
      ...calculatedDates,
    }));
  };

  // Handle employer selection
  const handleEmployerSelect = (selectedOption) => {
    if (!selectedOption) {
      setFormData((prev) => ({
        ...prev,
        employer_id: "",
        employer_name: "",
        employer_address: "",
        employer_phone: "",
        contact_person: "",
      }));
      return;
    }

    const employer = employers.find(
      (e) => e.employer_id === selectedOption.value
    );
    if (employer) {
      const fullAddress = [
        employer.address,
        employer.city,
        employer.state,
        employer.zip,
      ]
        .filter(Boolean)
        .join(", ");

      setFormData((prev) => ({
        ...prev,
        employer_id: employer.employer_id,
        employer_name: employer.company_name || "",
        employer_address: fullAddress,
        employer_phone: employer.phone || "",
        contact_person: employer.contact_name || "",
      }));
    }
  };

  // Handle placement selection - auto-fill job details
  const handlePlacementSelect = (selectedOption) => {
    if (!selectedOption) {
      setSelectedPlacement(null);
      return;
    }

    const placement = placements.find(
      (p) => p.placement_id === selectedOption.value
    );

    if (placement) {
      setSelectedPlacement(placement);

      // Auto-fill job details from placement
      setFormData((prev) => ({
        ...prev,
        job_title: placement.job_title || prev.job_title,
        job_duties: placement.job_duties || prev.job_duties,
        hourly_wage: placement.hourly_wage || prev.hourly_wage,
        hours_worked_weekly:
          placement.hours_per_week || prev.hours_worked_weekly,
        benefits_received: placement.benefits_offered || prev.benefits_received,
        date_placed:
          placement.placement_date?.split("T")[0] || prev.date_placed,
        start_date: placement.start_date?.split("T")[0] || prev.start_date,
      }));

      // If placement has employer, also fill employer info
      if (placement.employer_id) {
        const employer = employers.find(
          (e) => e.employer_id === placement.employer_id
        );
        if (employer) {
          const fullAddress = [
            employer.address,
            employer.city,
            employer.state,
            employer.zip,
          ]
            .filter(Boolean)
            .join(", ");

          setFormData((prev) => ({
            ...prev,
            employer_id: employer.employer_id,
            employer_name: employer.company_name || "",
            employer_address: fullAddress,
            employer_phone: employer.phone || "",
            contact_person: employer.contact_name || "",
          }));
        }
      }

      toast.success("Job details filled from placement record");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Gather signatures
    let w1ConsumerSig = week1ConsumerSig;
    let w1SpecialistSig = week1SpecialistSig;
    let d30ConsumerSig = days30ConsumerSig;
    let d30SpecialistSig = days30SpecialistSig;
    let d60ConsumerSig = days60ConsumerSig;
    let d60SpecialistSig = days60SpecialistSig;
    let closConsumerSig = closureConsumerSig;
    let closSpecialistSig = closureSpecialistSig;

    // Get current signatures from refs if available
    if (week1ConsumerRef.current) {
      w1ConsumerSig =
        week1ConsumerRef.current.getCurrentSignature?.() || w1ConsumerSig;
    }
    if (week1SpecialistRef.current) {
      w1SpecialistSig =
        week1SpecialistRef.current.getCurrentSignature?.() || w1SpecialistSig;
    }
    if (days30ConsumerRef.current) {
      d30ConsumerSig =
        days30ConsumerRef.current.getCurrentSignature?.() || d30ConsumerSig;
    }
    if (days30SpecialistRef.current) {
      d30SpecialistSig =
        days30SpecialistRef.current.getCurrentSignature?.() || d30SpecialistSig;
    }
    if (days60ConsumerRef.current) {
      d60ConsumerSig =
        days60ConsumerRef.current.getCurrentSignature?.() || d60ConsumerSig;
    }
    if (days60SpecialistRef.current) {
      d60SpecialistSig =
        days60SpecialistRef.current.getCurrentSignature?.() || d60SpecialistSig;
    }
    if (closureConsumerRef.current) {
      closConsumerSig =
        closureConsumerRef.current.getCurrentSignature?.() || closConsumerSig;
    }
    if (closureSpecialistRef.current) {
      closSpecialistSig =
        closureSpecialistRef.current.getCurrentSignature?.() || closSpecialistSig;
    }

    const payload = {
      ...formData,
      job_placement_id: jobPlacement.job_placement_id,
      consumer_id: consumer.consumer_id,
      tenant_id: tenantId,
      created_by: userId,
      // Include selected placement ID - if set, backend will update that placement instead of creating new
      selected_placement_id: selectedPlacement?.placement_id || null,
      // Only save signature dates if there's an actual signature
      review_week1_consumer_signature: w1ConsumerSig,
      review_week1_specialist_signature: w1SpecialistSig,
      review_week1_consumer_date: w1ConsumerSig ? (formData.week1_consumer_date || null) : null,
      review_week1_specialist_date: w1SpecialistSig ? (formData.week1_specialist_date || null) : null,
      review_30days_consumer_signature: d30ConsumerSig,
      review_30days_specialist_signature: d30SpecialistSig,
      review_30days_consumer_date: d30ConsumerSig ? (formData.days30_consumer_date || null) : null,
      review_30days_specialist_date: d30SpecialistSig ? (formData.days30_specialist_date || null) : null,
      review_60days_consumer_signature: d60ConsumerSig,
      review_60days_specialist_signature: d60SpecialistSig,
      review_60days_consumer_date: d60ConsumerSig ? (formData.days60_consumer_date || null) : null,
      review_60days_specialist_date: d60SpecialistSig ? (formData.days60_specialist_date || null) : null,
      review_closure_consumer_signature: closConsumerSig,
      review_closure_specialist_signature: closSpecialistSig,
      review_closure_consumer_date: closConsumerSig ? (formData.days90_consumer_date || null) : null,
      review_closure_specialist_date: closSpecialistSig ? (formData.days90_specialist_date || null) : null,
    };

    try {
      let followupId;
      
      if (followup) {
        await secureAxios.put(
          `${apiBase}/api/placement-followups/${followup.followup_id}`,
          payload
        );
        followupId = followup.followup_id;
        toast.success("Follow-up updated successfully");
      } else {
        const response = await secureAxios.post(`${apiBase}/api/placement-followups`, payload);
        followupId = response.data.followup_id;
        toast.success("Follow-up created successfully");
      }

      // Generate PDF and save to document library
      try {
        const pdfRes = await secureAxios.get(
          `${apiBase}/api/placement-followups/pdf/${followupId}`,
          { params: { tenant_id: tenantId } }
        );
        if (pdfRes.data?.pdf_url) {
          toast.info("PDF saved to Document Library");
        }
      } catch (pdfErr) {
        console.error("PDF generation error:", pdfErr);
        toast.warning("Follow-up saved but PDF could not be generated");
      }

      onSave?.();
    } catch (err) {
      console.error("Error saving follow-up:", err);
      toast.error(err.response?.data?.error || "Failed to save follow-up");
    } finally {
      setLoading(false);
    }
  };

  // Employer options for react-select
  const employerOptions = employers.map((emp) => ({
    value: emp.employer_id,
    label: `${emp.company_name}${
      emp.contact_name ? ` (${emp.contact_name})` : ""
    }`,
  }));

  // Placement options for react-select
  const placementOptions = placements.map((p) => ({
    value: p.placement_id,
    label: `${p.job_title || "Untitled"} - ${
      p.company_name || "No Employer"
    } (${
      p.start_date ? new Date(p.start_date).toLocaleDateString() : "No date"
    })`,
  }));

  const selectedEmployerOption = formData.employer_id
    ? employerOptions.find((opt) => opt.value === formData.employer_id)
    : null;

  return (
    <div className="container my-4">
    <div className="placement-followup-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0 h6">
          <i className="bi bi-clipboard-check me-2"></i>
          {followup ? "Edit" : "Create"} CRP-32: Job Placement Follow-Up
        </h5>
        <button className="btn btn-outline-secondary" onClick={onCancel}>
          <i className="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>

      {/* Consumer Info Banner */}
      <div className="alert alert-info mb-4">
        <strong>Consumer:</strong> {consumer?.name || "Unknown"}{" "}
        <span className="ms-3">
          <strong>CRP-30:</strong>{" "}
          {jobPlacement?.vocational_goal || "No vocational goal set"}
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="card mb-4">
          <div className="card-header bg-primary text-white py-2">
            <i className="bi bi-info-circle me-2"></i>
            Basic Information
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-md-6">
                <label className="form-label">Placement Specialist</label>
                <CreatableSelect
                  options={jobCoaches.map((jc) => ({
                    value: jc.name,
                    label: jc.name,
                  }))}
                  value={
                    formData.placement_specialist
                      ? {
                          value: formData.placement_specialist,
                          label: formData.placement_specialist,
                        }
                      : null
                  }
                  onChange={(opt) =>
                    setFormData((prev) => ({
                      ...prev,
                      placement_specialist: opt?.value || "",
                    }))
                  }
                  onCreateOption={(inputValue) =>
                    setFormData((prev) => ({
                      ...prev,
                      placement_specialist: inputValue,
                    }))
                  }
                  isClearable
                  placeholder="Type a name or select from list..."
                  isDisabled={loading}
                  formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                />
                <small className="text-muted">
                  Defaults to you. Type a custom name or select from job coaches.
                </small>
              </div>

              <div className="col-md-6">
                <label className="form-label">Referring Counselor</label>
                <input
                  type="text"
                  name="referring_counselor"
                  className="form-control form-control-sm"
                  value={formData.referring_counselor}
                  onChange={handleChange}
                  disabled={loading}
                />
                <small className="text-muted">
                  Pre-filled from consumer's counselor
                </small>
              </div>

              <div className="col-md-4">
                <label className="form-label">Date Placed</label>
                <input
                  type="date"
                  name="date_placed"
                  className="form-control form-control-sm"
                  value={formData.date_placed}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  className="form-control form-control-sm"
                  value={formData.start_date}
                  onChange={handleStartDateChange}
                  disabled={loading}
                />
                <small className="text-muted">
                  Follow-up dates auto-calculate from this
                </small>
              </div>

              <div className="col-md-4">
                <label className="form-label">Completion Date</label>
                <input
                  type="date"
                  name="completion_date"
                  className="form-control form-control-sm"
                  value={formData.completion_date}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Closure Plan</label>
                <textarea
                  name="closure_plan"
                  className="form-control form-control-sm"
                  value={formData.closure_plan}
                  onChange={handleChange}
                  rows="2"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Job Details - Now first */}
        <div className="card mb-4">
          <div 
            className="card-header bg-info text-white d-flex justify-content-between align-items-center"
            style={{ cursor: 'pointer' }}
            onClick={() => setJobDetailsCollapsed(!jobDetailsCollapsed)}
          >
            <span>
              <i className="bi bi-briefcase me-2"></i>
              Job Details
            </span>
            <i className={`bi ${jobDetailsCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
          </div>
          {!jobDetailsCollapsed && (
            <div className="card-body p-2 p-md-3">
              {/* Select Placement Dropdown - Always visible */}
              <div className="mb-3 p-3 bg-light rounded">
                <label className="form-label fw-bold">
                  <i className="bi bi-link-45deg me-1"></i>
                  Select from Existing Placements
                </label>
                {placements.length > 0 ? (
                  <>
                    <Select
                      options={placementOptions}
                      value={
                        selectedPlacement
                          ? placementOptions.find(
                              (opt) => opt.value === selectedPlacement.placement_id
                            )
                          : null
                      }
                      onChange={handlePlacementSelect}
                      isClearable
                      placeholder="Select a placement to auto-fill job details..."
                      isDisabled={loading}
                    />
                    <small className="text-muted">
                      Selecting a placement will auto-fill job title, wage, hours,
                      duties, and benefits
                    </small>
                  </>
                ) : (
                  <div className="alert alert-secondary mb-0 py-2">
                    <i className="bi bi-info-circle me-2"></i>
                    <small>
                      No placements found for this consumer. You can create a placement record in{" "}
                      <strong>Placement Management &gt; Placements</strong>, or manually enter the job details below.
                    </small>
                  </div>
                )}
              </div>

              <div className="row g-2 g-md-3">
                <div className="col-md-4">
                  <label className="form-label">Job Title</label>
                  <input
                    type="text"
                    name="job_title"
                    className="form-control form-control-sm"
                    value={formData.job_title}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Hourly Wage ($)</label>
                  <input
                    type="number"
                    name="hourly_wage"
                    className="form-control form-control-sm"
                    value={formData.hourly_wage}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    disabled={loading}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Hours Worked Weekly</label>
                  <input
                    type="number"
                    name="hours_worked_weekly"
                    className="form-control form-control-sm"
                    value={formData.hours_worked_weekly}
                    onChange={handleChange}
                    step="0.5"
                    min="0"
                    disabled={loading}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Job Duties (See Attached)</label>
                  <textarea
                    name="job_duties"
                    className="form-control form-control-sm"
                    value={formData.job_duties}
                    onChange={handleChange}
                    rows="3"
                    disabled={loading}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">
                    Benefits Received (Incentives)
                  </label>
                  <textarea
                    name="benefits_received"
                    className="form-control form-control-sm"
                    value={formData.benefits_received}
                    onChange={handleChange}
                    rows="2"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Employer Information - Now second */}
        <div className="card mb-4">
          <div 
            className="card-header bg-success text-white d-flex justify-content-between align-items-center"
            style={{ cursor: 'pointer' }}
          >
            <span 
              onClick={() => setEmployerCollapsed(!employerCollapsed)}
              style={{ flex: 1, cursor: 'pointer' }}
            >
              <i className="bi bi-building me-2"></i>
              Employer Information
              <i className={`bi ${employerCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'} ms-2`}></i>
            </span>
            {!employerCollapsed && (
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className={`btn ${
                    entryMode === "existing" ? "btn-light" : "btn-outline-light"
                  }`}
                  onClick={() => setEntryMode("existing")}
                >
                  <i className="bi bi-search me-1"></i> Select Existing
                </button>
                <button
                  type="button"
                  className={`btn ${
                    entryMode === "manual" ? "btn-light" : "btn-outline-light"
                  }`}
                  onClick={() => setEntryMode("manual")}
                >
                  <i className="bi bi-pencil me-1"></i> Manual Entry
                </button>
              </div>
            )}
          </div>
          {!employerCollapsed && (
            <div className="card-body p-2 p-md-3">
              {entryMode === "existing" && (
                <div className="mb-3">
                  <label className="form-label">Select Employer</label>
                  <Select
                    options={employerOptions}
                    value={selectedEmployerOption}
                    onChange={handleEmployerSelect}
                    isClearable
                    placeholder="Search for an employer..."
                    isDisabled={loading}
                  />
                  <small className="text-muted">
                    Select an existing employer to auto-fill the fields below
                  </small>
                </div>
              )}

              <div className="row g-2 g-md-3">
                <div className="col-md-6">
                  <label className="form-label">Employer's Name</label>
                  <input
                    type="text"
                    name="employer_name"
                    className="form-control form-control-sm"
                    value={formData.employer_name}
                    onChange={handleChange}
                    disabled={loading || entryMode === "existing"}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Employer's Phone</label>
                  <input
                    type="text"
                    name="employer_phone"
                    className="form-control form-control-sm"
                    value={formData.employer_phone}
                    onChange={handleChange}
                    disabled={loading || entryMode === "existing"}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Employer's Address</label>
                  <input
                    type="text"
                    name="employer_address"
                    className="form-control form-control-sm"
                    value={formData.employer_address}
                    onChange={handleChange}
                    disabled={loading || entryMode === "existing"}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Contact Person</label>
                  <input
                    type="text"
                    name="contact_person"
                    className="form-control form-control-sm"
                    value={formData.contact_person}
                    onChange={handleChange}
                    disabled={loading || entryMode === "existing"}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Immediate Supervisor</label>
                  <input
                    type="text"
                    name="immediate_supervisor"
                    className="form-control form-control-sm"
                    value={formData.immediate_supervisor}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Follow-Up Reviews */}
        <div className="card mb-4">
          <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-calendar-check me-2"></i>
              Follow-Up Reviews
            </span>
            <button
              type="button"
              className={`btn ${showSignatures ? 'btn-dark' : 'btn-primary'} fw-bold`}
              onClick={() => setShowSignatures(!showSignatures)}
              style={{ 
                minWidth: '160px',
                boxShadow: showSignatures ? 'none' : '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              <i className={`bi ${showSignatures ? 'bi-eye-slash' : 'bi-pen'} me-1`}></i>
              {showSignatures ? 'Hide Signatures' : 'Show Signatures'}
            </button>
          </div>
          <div className="card-body p-2 p-md-3">
            {/* Week 1 */}
            <div className="border-bottom pb-3 mb-3">
              <h6>
                <i className="bi bi-1-circle me-2"></i>Week 1 Review
                {formData.week1_date && (
                  <small className="text-muted ms-2">
                    (Target: {formatDateForDisplay(formData.week1_date)})
                  </small>
                )}
              </h6>
              <div className="row g-2 g-md-3">
                <div className="col-md-3">
                  <label className="form-label">Target Date</label>
                  <input
                    type="date"
                    name="week1_date"
                    className="form-control form-control-sm"
                    value={formData.week1_date}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="col-md-9">
                  <label className="form-label">Comments</label>
                  <textarea
                    name="week1_comments"
                    className="form-control form-control-sm"
                    value={formData.week1_comments}
                    onChange={handleChange}
                    rows="2"
                    disabled={loading}
                  />
                </div>
              </div>
              {showSignatures && (
                <div className="row g-3 mt-2">
                  <div className="col-md-6">
                    <SignatureField
                      ref={week1ConsumerRef}
                      label="Consumer Signature"
                      value={week1ConsumerSig}
                      onChange={setWeek1ConsumerSig}
                      penColor="blue"
                      disabled={loading}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <input
                        type="date"
                        name="week1_consumer_date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '160px' }}
                        value={formData.week1_consumer_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFormData(prev => ({ ...prev, week1_consumer_date: new Date().toISOString().split('T')[0] }))}
                        disabled={loading}
                      >
                        Today
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <SignatureField
                      ref={week1SpecialistRef}
                      label="Specialist Signature"
                      value={week1SpecialistSig}
                      onChange={setWeek1SpecialistSig}
                      penColor="blue"
                      disabled={loading}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <input
                        type="date"
                        name="week1_specialist_date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '160px' }}
                        value={formData.week1_specialist_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFormData(prev => ({ ...prev, week1_specialist_date: new Date().toISOString().split('T')[0] }))}
                        disabled={loading}
                      >
                        Today
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 30 Days */}
            <div className="border-bottom pb-3 mb-3">
              <h6>
                <i className="bi bi-calendar me-2"></i>30 Days Review
                {formData.days30_date && (
                  <small className="text-muted ms-2">
                    (Target: {formatDateForDisplay(formData.days30_date)})
                  </small>
                )}
              </h6>
              <div className="row g-2 g-md-3">
                <div className="col-md-3">
                  <label className="form-label">Target Date</label>
                  <input
                    type="date"
                    name="days30_date"
                    className="form-control form-control-sm"
                    value={formData.days30_date}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="col-md-9">
                  <label className="form-label">Comments</label>
                  <textarea
                    name="days30_comments"
                    className="form-control form-control-sm"
                    value={formData.days30_comments}
                    onChange={handleChange}
                    rows="2"
                    disabled={loading}
                  />
                </div>
              </div>
              {showSignatures && (
                <div className="row g-3 mt-2">
                  <div className="col-md-6">
                    <SignatureField
                      ref={days30ConsumerRef}
                      label="Consumer Signature"
                      value={days30ConsumerSig}
                      onChange={setDays30ConsumerSig}
                      penColor="blue"
                      disabled={loading}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <input
                        type="date"
                        name="days30_consumer_date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '160px' }}
                        value={formData.days30_consumer_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFormData(prev => ({ ...prev, days30_consumer_date: new Date().toISOString().split('T')[0] }))}
                        disabled={loading}
                      >
                        Today
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <SignatureField
                      ref={days30SpecialistRef}
                      label="Specialist Signature"
                      value={days30SpecialistSig}
                      onChange={setDays30SpecialistSig}
                      penColor="blue"
                      disabled={loading}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <input
                        type="date"
                        name="days30_specialist_date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '160px' }}
                        value={formData.days30_specialist_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFormData(prev => ({ ...prev, days30_specialist_date: new Date().toISOString().split('T')[0] }))}
                        disabled={loading}
                      >
                        Today
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 60 Days */}
            <div className="border-bottom pb-3 mb-3">
              <h6>
                <i className="bi bi-calendar me-2"></i>60 Days Review
                {formData.days60_date && (
                  <small className="text-muted ms-2">
                    (Target: {formatDateForDisplay(formData.days60_date)})
                  </small>
                )}
              </h6>
              <div className="row g-2 g-md-3">
                <div className="col-md-3">
                  <label className="form-label">Target Date</label>
                  <input
                    type="date"
                    name="days60_date"
                    className="form-control form-control-sm"
                    value={formData.days60_date}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="col-md-9">
                  <label className="form-label">Comments</label>
                  <textarea
                    name="days60_comments"
                    className="form-control form-control-sm"
                    value={formData.days60_comments}
                    onChange={handleChange}
                    rows="2"
                    disabled={loading}
                  />
                </div>
              </div>
              {showSignatures && (
                <div className="row g-3 mt-2">
                  <div className="col-md-6">
                    <SignatureField
                      ref={days60ConsumerRef}
                      label="Consumer Signature"
                      value={days60ConsumerSig}
                      onChange={setDays60ConsumerSig}
                      penColor="blue"
                      disabled={loading}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <input
                        type="date"
                        name="days60_consumer_date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '160px' }}
                        value={formData.days60_consumer_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFormData(prev => ({ ...prev, days60_consumer_date: new Date().toISOString().split('T')[0] }))}
                        disabled={loading}
                      >
                        Today
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <SignatureField
                      ref={days60SpecialistRef}
                      label="Specialist Signature"
                      value={days60SpecialistSig}
                      onChange={setDays60SpecialistSig}
                      penColor="blue"
                      disabled={loading}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <input
                        type="date"
                        name="days60_specialist_date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '160px' }}
                        value={formData.days60_specialist_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFormData(prev => ({ ...prev, days60_specialist_date: new Date().toISOString().split('T')[0] }))}
                        disabled={loading}
                      >
                        Today
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 90 Days / Closure */}
            <div>
              <h6>
                <i className="bi bi-calendar-check me-2"></i>90 Days Review / Closure
                {formData.days90_date && (
                  <small className="text-muted ms-2">
                    (Target: {formatDateForDisplay(formData.days90_date)})
                  </small>
                )}
              </h6>
              <div className="row g-2 g-md-3">
                <div className="col-md-3">
                  <label className="form-label">Target Date</label>
                  <input
                    type="date"
                    name="days90_date"
                    className="form-control form-control-sm"
                    value={formData.days90_date}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
                <div className="col-md-9">
                  <label className="form-label">Comments</label>
                  <textarea
                    name="days90_comments"
                    className="form-control form-control-sm"
                    value={formData.days90_comments}
                    onChange={handleChange}
                    rows="2"
                    disabled={loading}
                  />
                </div>
              </div>
              {showSignatures && (
                <div className="row g-3 mt-2">
                  <div className="col-md-6">
                    <SignatureField
                      ref={closureConsumerRef}
                      label="Consumer Signature"
                      value={closureConsumerSig}
                      onChange={setClosureConsumerSig}
                      penColor="blue"
                      disabled={loading}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <input
                        type="date"
                        name="days90_consumer_date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '160px' }}
                        value={formData.days90_consumer_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFormData(prev => ({ ...prev, days90_consumer_date: new Date().toISOString().split('T')[0] }))}
                        disabled={loading}
                      >
                        Today
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <SignatureField
                      ref={closureSpecialistRef}
                      label="Specialist Signature"
                      value={closureSpecialistSig}
                      onChange={setClosureSpecialistSig}
                      penColor="blue"
                      disabled={loading}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <input
                        type="date"
                        name="days90_specialist_date"
                        className="form-control form-control-sm"
                        style={{ maxWidth: '160px' }}
                        value={formData.days90_specialist_date}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFormData(prev => ({ ...prev, days90_specialist_date: new Date().toISOString().split('T')[0] }))}
                        disabled={loading}
                      >
                        Today
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                {followup ? "Update" : "Create"} Follow-Up
              </>
            )}
          </button>
        </div>
      </form>
    </div>
    </div>
  );
};

export default PlacementFollowupForm;